import { useEffect, useState, useCallback, useRef } from 'react';
import { db } from './firebase';
import {
  collection, doc, getDoc, getDocs, setDoc, deleteDoc, writeBatch, onSnapshot, query, orderBy, limit
} from 'firebase/firestore';

// ref：題目在 Firestore 的位置（科目 ID、主題、題目文件 ID）。匯入的自訂題目沒有 ref，以題目文字識別。
export type QRef = { s:string; t:string; id:string };
export type WrongItem = { t:string; q?:string; o?:string[]; a?:number; e?:string; p?:string[][]; ref?:QRef; streak?:number };  // streak：進錯題本後連續答對次數
export type RecordItem = {
  nickname:string;
  sub:string;        // 科目 ID
  subName?:string;   // 紀錄當下的科目名稱
  topic:string; mode:string;
  pct:number; score:number; total:number; timeTaken:number; date:string;
};

// 錯題文件 ID：有題目位置就用位置；否則用題目文字的 FNV-1a hash
function wrongKey(item: WrongItem): string {
  if (item.ref) return `q_${item.ref.s}__${item.ref.t}__${item.ref.id}`;
  const s = item.q || item.p?.[0]?.[0] || '';
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return 'w' + h.toString(16).padStart(8, '0') + '_' + s.length.toString(16);
}

export function useUserData(uid: string | null) {
  const [wrongs, setWrongs]   = useState<WrongItem[]>([]);
  const [records, setRecords] = useState<RecordItem[]>([]);
  const removeWrongRef = useRef<(w: WrongItem) => Promise<void>>(async () => {});  // 供 markCorrect 呼叫（removeWrong 定義在後）

  // Real-time wrongs listener
  useEffect(() => {
    if (!uid) {
      setWrongs([]);
      try { setWrongs(JSON.parse(localStorage.getItem('gsat_wrongs') || '[]')); } catch {}
      return;
    }
    const col = collection(db, 'users', uid, 'wrongs');
    const unsub = onSnapshot(col, snap => {
      setWrongs(snap.docs.map(d => d.data() as WrongItem));
    });
    return unsub;
  }, [uid]);

  // Real-time records listener (last 20, ordered by date desc)
  useEffect(() => {
    if (!uid) {
      setRecords([]);
      try { setRecords(JSON.parse(localStorage.getItem('gsat_records') || '[]')); } catch {}
      return;
    }
    const col = collection(db, 'users', uid, 'records');
    const q   = query(col, orderBy('date','desc'), limit(20));
    const unsub = onSnapshot(q, snap => {
      setRecords(snap.docs.map(d => d.data() as RecordItem));
    });
    return unsub;
  }, [uid]);

  const addWrong = useCallback(async (item: WrongItem) => {
    if (!uid) {
      const cur: WrongItem[] = JSON.parse(localStorage.getItem('gsat_wrongs') || '[]');
      // 已在錯題本：重設連續答對次數；否則加入
      const k = wrongKey(item);
      const next = cur.some(w => wrongKey(w) === k)
        ? cur.map(w => wrongKey(w) === k ? { ...w, streak: 0 } : w)
        : [...cur, { ...item, streak: 0 }];
      localStorage.setItem('gsat_wrongs', JSON.stringify(next));
      setWrongs(next); // 即時更新畫面（訪客模式）
      return;
    }
    await setDoc(doc(db, 'users', uid, 'wrongs', wrongKey(item)), { ...item, streak: 0 });
  }, [uid]);

  // 答對錯題本中的題目：連續答對達 need 次才移出，否則累計次數
  // 回傳 true 表示這題剛移出錯題本（學會了）
  const markCorrect = useCallback(async (item: WrongItem, need: number): Promise<boolean> => {
    const k = wrongKey(item);
    const cur = wrongs.find(w => wrongKey(w) === k);
    if (!cur) return false;
    const streak = (cur.streak ?? 0) + 1;
    if (streak >= need) { await removeWrongRef.current(cur); return true; }
    if (!uid) {
      const list: WrongItem[] = JSON.parse(localStorage.getItem('gsat_wrongs') || '[]');
      const next = list.map(w => wrongKey(w) === k ? { ...w, streak } : w);
      localStorage.setItem('gsat_wrongs', JSON.stringify(next));
      setWrongs(next);
      return false;
    }
    await setDoc(doc(db, 'users', uid, 'wrongs', k), { streak }, { merge: true });
    return false;
  }, [uid, wrongs]);

  const removeWrong = useCallback(async (item: WrongItem) => {
    if (!uid) {
      const cur: WrongItem[] = JSON.parse(localStorage.getItem('gsat_wrongs') || '[]');
      const next = cur.filter(w => wrongKey(w) !== wrongKey(item));
      localStorage.setItem('gsat_wrongs', JSON.stringify(next));
      setWrongs(next); // 即時更新畫面（訪客模式）
      return;
    }
    // 同時刪除新版 hash key 與舊版截斷 key（向下相容既有資料）
    const legacyKey = btoa(encodeURIComponent(item.q || item.p?.[0]?.[0] || '')).slice(0,20);
    await Promise.all([
      deleteDoc(doc(db, 'users', uid, 'wrongs', wrongKey(item))),
      deleteDoc(doc(db, 'users', uid, 'wrongs', legacyKey)),
    ]);
  }, [uid]);
  removeWrongRef.current = removeWrong;

  const clearWrongs = useCallback(async () => {
    if (!uid) { localStorage.removeItem('gsat_wrongs'); setWrongs([]); return; }
    const col  = collection(db, 'users', uid, 'wrongs');
    const snap = await getDocs(col);
    const batch = writeBatch(db);
    snap.docs.forEach(d => batch.delete(d.ref));
    await batch.commit();
  }, [uid]);

  // 清除指定的多筆錯題（錯題本分塊清除）
  const removeWrongs = useCallback(async (items: WrongItem[]) => {
    const keys = new Set(items.map(wrongKey));
    if (!uid) {
      const cur: WrongItem[] = JSON.parse(localStorage.getItem('gsat_wrongs') || '[]');
      const next = cur.filter(w => !keys.has(wrongKey(w)));
      localStorage.setItem('gsat_wrongs', JSON.stringify(next));
      setWrongs(next);
      return;
    }
    const list = Array.from(keys);
    for (let i = 0; i < list.length; i += 400) {
      const batch = writeBatch(db);
      list.slice(i, i + 400).forEach(k => batch.delete(doc(db, 'users', uid, 'wrongs', k)));
      await batch.commit();
    }
  }, [uid]);

  const addRecord = useCallback(async (rec: RecordItem) => {
    if (!uid) {
      const cur: RecordItem[] = JSON.parse(localStorage.getItem('gsat_records') || '[]');
      localStorage.setItem('gsat_records', JSON.stringify([rec, ...cur].slice(0,20)));
      return;
    }
    const key = `${Date.now()}`;
    await setDoc(doc(db, 'users', uid, 'records', key), rec);
  }, [uid]);

  // 開始複習前以題目位置讀取最新內容（題目修正後同步）；題目已刪除則移出錯題本
  const refreshWrongs = useCallback(async (items: WrongItem[]): Promise<WrongItem[]> => {
    const out: WrongItem[] = [];
    const gone: WrongItem[] = [];
    await Promise.all(items.map(async (w, i) => {
      if (!w.ref) { out[i] = w; return; }
      try {
        const d = await getDoc(doc(db, 'questions', w.ref.s, 'topics', w.ref.t, 'items', w.ref.id));
        if (!d.exists()) { gone.push(w); return; }
        const fresh = { ...(d.data() as WrongItem), ref: w.ref };
        out[i] = fresh;
        if (uid && JSON.stringify(fresh) !== JSON.stringify(w)) setDoc(doc(db, 'users', uid, 'wrongs', wrongKey(w)), fresh).catch(() => {});
      } catch { out[i] = w; }   // 離線或讀取失敗：沿用存下的內容
    }));
    for (const w of gone) await removeWrong(w);
    return out.filter(Boolean);
  }, [uid, removeWrong]);

  return { wrongs, records, addWrong, removeWrong, removeWrongs, markCorrect, clearWrongs, addRecord, refreshWrongs };
}
